import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  AgentsApiError,
  BreadConnectedAgent,
  disconnectAgent,
  getMcpConnectorUrl,
  listConnectedAgents,
} from "../firebaseio/agentsIo";
import { RyeButton } from "../rye/RyeButton";
import { RyeModalContent, useRyeModal } from "../rye/RyeModal";
import { RyeSkeleton } from "../rye/RyeSkeleton";
import { buildClasses } from "../utils/buildClasses";
import { SettingRow } from "./SettingRow";

const COPIED_FEEDBACK_MS = 2000;

export function AgentsSettings(): JSX.Element {
  return (
    <div className={buildClasses("flex", "flex-col", "gap-8")}>
      <AgentsIntro />
      <ConnectorUrl />
      <HowToConnect />
      <ConnectedAgents />
    </div>
  );
}

function SectionHeading({ children }: { children: string }): JSX.Element {
  return (
    <h3
      className={buildClasses(
        "text-lg",
        "font-semibold",
        "text-on-surface-800",
        "mb-2",
      )}
    >
      {children}
    </h3>
  );
}

function AgentsIntro(): JSX.Element {
  return (
    <div
      className={buildClasses(
        "flex",
        "flex-col",
        "gap-3",
        "max-w-[40rem]",
        "text-sm",
        "text-on-surface-600",
      )}
    >
      <p>
        Connect your own AI assistant to Bread. It can see your accounts,
        balances, transactions, categories, and budgets. It can also fill in or
        fix categories on transactions you haven't reviewed, create, add and
        remove tags, set up and edit categories and budgets when you ask, and
        sync with your banks. It can't delete anything, override your rules, or
        recategorize transactions you've already reviewed, and categories it
        fills in don't count until you review them. Each app asks for your
        approval on a Bread sign-in page, and you can disconnect it here at any
        time.
      </p>
      <p>
        Bread has no built-in categorization AI on purpose: going through your
        own transactions is how you come to understand and trust your numbers.
        An assistant that already knows your calendar and email can help with
        that review, without competing with an AI built into the app. Bread's
        charts are there to use, and your assistant can link you to them.
      </p>
    </div>
  );
}

function ConnectorUrl(): JSX.Element {
  const url = getMcpConnectorUrl();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const resetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimeout.current !== null) clearTimeout(resetTimeout.current);
    },
    [],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopyFailed(false);
      setCopied(true);
      if (resetTimeout.current !== null) clearTimeout(resetTimeout.current);
      resetTimeout.current = setTimeout(
        () => setCopied(false),
        COPIED_FEEDBACK_MS,
      );
    } catch (err) {
      console.error("Failed to copy connector URL:", err);
      setCopyFailed(true);
    }
  }

  return (
    <div>
      <SectionHeading>Connector URL</SectionHeading>
      <div className={buildClasses("flex", "items-center", "gap-3")}>
        <code
          aria-label="Connector URL"
          className={buildClasses(
            "min-w-0",
            "truncate",
            "rounded-md",
            "border",
            "border-on-surface-200",
            "bg-on-surface-50",
            "px-3",
            "py-2",
            "font-mono",
            "text-sm",
            "text-on-surface-800",
          )}
        >
          {url}
        </code>
        <RyeButton
          icon={copied ? "check" : "content_copy"}
          text={copied ? "Copied" : "Copy"}
          onClick={() => void copy()}
        />
      </div>
      {copyFailed && (
        <p
          role="alert"
          className={buildClasses("mt-2", "text-sm", "text-red-800")}
        >
          Couldn't copy automatically. Select the URL and copy it instead.
        </p>
      )}
    </div>
  );
}

function HowToConnect(): JSX.Element {
  return (
    <div>
      <SectionHeading>How to connect</SectionHeading>
      <ul
        className={buildClasses(
          "list-disc",
          "pl-5",
          "space-y-1",
          "text-sm",
          "text-on-surface-600",
        )}
      >
        <li>
          <strong className={buildClasses("font-medium")}>Claude:</strong>{" "}
          Customize → Connectors → Add custom connector, then paste the URL.
        </li>
        <li>
          <strong className={buildClasses("font-medium")}>Gemini:</strong>{" "}
          Settings → Connected Apps → Custom apps, then paste the URL.
        </li>
      </ul>
    </div>
  );
}

type AgentsLoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; agents: BreadConnectedAgent[] };

function describeError(error: unknown, action: string): string {
  if (error instanceof AgentsApiError && error.status === 401) {
    return "We couldn't verify your session. Reload the page or sign in again.";
  }
  return `We couldn't ${action}. Check your connection and try again.`;
}

function ConnectedAgents(): JSX.Element {
  const { openModal } = useRyeModal();
  const [state, setState] = useState<AgentsLoadState>({ status: "loading" });
  const [disconnecting, setDisconnecting] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const agents = await listConnectedAgents();
      if (isMounted.current) setState({ status: "loaded", agents });
    } catch (error) {
      console.error("Failed to load connected agents:", error);
      if (isMounted.current) {
        setState({
          status: "error",
          message: describeError(error, "load your connected agents"),
        });
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function disconnect(agent: BreadConnectedAgent) {
    setDisconnectError(null);
    setDisconnecting((current) => new Set(current).add(agent.grantId));
    try {
      await disconnectAgent(agent.grantId);
      if (!isMounted.current) return;
      setState((current) =>
        current.status === "loaded"
          ? {
              status: "loaded",
              agents: current.agents.filter((a) => a.grantId !== agent.grantId),
            }
          : current,
      );
    } catch (error) {
      console.error("Failed to disconnect agent:", error);
      if (isMounted.current) {
        setDisconnectError(
          describeError(error, `disconnect ${agent.clientName}`),
        );
      }
    } finally {
      if (isMounted.current) {
        setDisconnecting((current) => {
          const next = new Set(current);
          next.delete(agent.grantId);
          return next;
        });
      }
    }
  }

  function confirmDisconnect(agent: BreadConnectedAgent) {
    openModal(
      <div className={buildClasses("w-[28rem]", "max-w-[calc(100vw-5.5rem)]")}>
        <RyeModalContent
          title={`Disconnect ${agent.clientName}?`}
          content={
            <p className={buildClasses("text-sm")}>
              {agent.clientName} will lose access to your Bread data right away.
              You can connect it again later.
            </p>
          }
          actionButtons={[
            { text: "Never mind", vibe: "subdued", action: () => {} },
            {
              text: "Disconnect",
              vibe: "danger",
              variant: "filled",
              action: () => void disconnect(agent),
            },
          ]}
        />
      </div>,
    );
  }

  return (
    <div>
      <SectionHeading>Connected agents</SectionHeading>
      {state.status === "loading" && (
        <div role="status" aria-busy="true">
          <span className={buildClasses("sr-only")}>
            Loading connected agents…
          </span>
          <RyeSkeleton className={buildClasses("w-64", "h-5")} />
        </div>
      )}
      {state.status === "error" && (
        <div className={buildClasses("flex", "items-center", "gap-4")}>
          <p role="alert" className={buildClasses("text-sm", "text-red-800")}>
            {state.message}
          </p>
          <RyeButton text="Try again" onClick={() => void load()} />
        </div>
      )}
      {state.status === "loaded" && state.agents.length === 0 && (
        <p className={buildClasses("text-sm", "text-on-surface-600")}>
          No agents connected yet
        </p>
      )}
      {state.status === "loaded" &&
        state.agents.map((agent) => (
          <SettingRow
            key={agent.grantId}
            label={agent.clientName}
            description={
              <>
                {agent.clientDomain && <>{agent.clientDomain} · </>}
                Connected {dayjs(agent.connectedAt).format("MMM D, YYYY")}
              </>
            }
            picker={
              <RyeButton
                text="Disconnect"
                vibe="danger"
                disabled={disconnecting.has(agent.grantId)}
                onClick={() => confirmDisconnect(agent)}
              />
            }
          />
        ))}
      {disconnectError && (
        <p
          role="alert"
          className={buildClasses("mt-2", "text-sm", "text-red-800")}
        >
          {disconnectError}
        </p>
      )}
    </div>
  );
}
